/**
 * CL-381A / CL-381C — Real estate portal access requests (collection: realEstateAccessRequests).
 */

export const RE_ACCESS_REQUEST_SOURCE = "client_real_estate_no_access";

export const RE_ACCESS_REQUEST_STATUS = {
  PENDING: "pending",
  REVIEWED: "reviewed",
  APPROVED: "approved",
  DISMISSED: "dismissed",
  DENIED: "denied",
};

const TERMINAL_STATUSES = new Set([
  RE_ACCESS_REQUEST_STATUS.REVIEWED,
  RE_ACCESS_REQUEST_STATUS.APPROVED,
  RE_ACCESS_REQUEST_STATUS.DISMISSED,
  RE_ACCESS_REQUEST_STATUS.DENIED,
]);

function safeStr(value) {
  return String(value ?? "").trim();
}

function normalizeStatus(value) {
  return safeStr(value).toLowerCase() || RE_ACCESS_REQUEST_STATUS.PENDING;
}

export function isPendingRealEstateAccessRequest(row) {
  return normalizeStatus(row?.status) === RE_ACCESS_REQUEST_STATUS.PENDING;
}

export function isTerminalRealEstateAccessRequest(row) {
  return TERMINAL_STATUSES.has(normalizeStatus(row?.status));
}

/** Admin dashboard / contacts — only actionable pending rows. */
export function filterPendingRealEstateAccessRequests(rows) {
  return (Array.isArray(rows) ? rows : []).filter((r) => isPendingRealEstateAccessRequest(r));
}

/** Firestore create payload (caller adds createdAt / updatedAt). */
export function buildRealEstateAccessRequestDoc({ userId, email, displayName, source }) {
  return {
    userId: safeStr(userId),
    email: safeStr(email),
    displayName: safeStr(displayName) || null,
    status: RE_ACCESS_REQUEST_STATUS.PENDING,
    source: safeStr(source) || RE_ACCESS_REQUEST_SOURCE,
  };
}

/** Admin status update fields (caller adds reviewedAt / updatedAt timestamps). */
export function buildReAccessRequestAdminStatusUpdate(status, reviewedByUid) {
  const next = normalizeStatus(status);
  if (!TERMINAL_STATUSES.has(next) && next !== RE_ACCESS_REQUEST_STATUS.PENDING) {
    throw new Error(`Invalid real estate access request status: ${status}`);
  }
  return {
    status: next,
    reviewedBy: safeStr(reviewedByUid) || null,
  };
}

export function reAccessRequestStatusLabel(status) {
  const key = normalizeStatus(status);
  const labels = {
    pending: "Pending",
    reviewed: "Reviewed",
    approved: "Approved",
    dismissed: "Dismissed",
    denied: "Denied",
  };
  return labels[key] || key;
}

export function sortReAccessRequestsNewestFirst(rows) {
  return [...(rows || [])].sort((a, b) => {
    const as = a?.createdAt?.seconds ?? a?.createdAt?.toDate?.()?.getTime?.() ?? 0;
    const bs = b?.createdAt?.seconds ?? b?.createdAt?.toDate?.()?.getTime?.() ?? 0;
    return bs - as;
  });
}

export function findPendingRealEstateAccessRequest(rows) {
  return sortReAccessRequestsNewestFirst(rows).find((r) => isPendingRealEstateAccessRequest(r)) || null;
}

/** Map userId → pending request (newest wins). */
export function pendingReAccessRequestByUserId(rows) {
  const map = new Map();
  for (const row of sortReAccessRequestsNewestFirst(filterPendingRealEstateAccessRequests(rows))) {
    const uid = safeStr(row?.userId);
    if (!uid || map.has(uid)) continue;
    map.set(uid, row);
  }
  return map;
}
